/**
 * WhatsApp Carbon Tracking Service
 * Handles WhatsApp webhook processing for carbon data updates
 */

interface TransportEntry {
  mode: 'car' | 'bus' | 'flight' | 'bike' | 'walk' | 'train';
  distance: string;
}

interface HomeEntry {
  electricity?: string;
  gas?: string;
  water?: string;
}

interface FoodEntry {
  type: 'meat' | 'vegetarian' | 'vegan' | 'seafood';
  servings: string;
}

interface CarbonLogData {
  transport: TransportEntry[];
  home: HomeEntry;
  food: FoodEntry[];
  goods: string;
  date: string;
}

interface WhatsAppMessage {
  from: string;
  body: string;
  timestamp: number;
}

export class WhatsAppCarbonService {
  private static readonly TRANSPORT_KEYWORDS = {
    car: ['car', 'drive', 'drove', 'driving', 'vehicle'],
    bus: ['bus', 'public transport', 'transit'],
    flight: ['flight', 'fly', 'plane', 'airplane', 'aircraft'],
    bike: ['bike', 'bicycle', 'cycling', 'cycle'],
    walk: ['walk', 'walked', 'walking', 'foot'],
    train: ['train', 'railway', 'metro', 'subway']
  };

  private static readonly FOOD_KEYWORDS = {
    meat: ['meat', 'beef', 'pork', 'chicken', 'lamb', 'turkey', 'steak', 'burger'],
    vegetarian: ['vegetarian', 'veggie', 'dairy', 'cheese', 'milk', 'eggs'],
    vegan: ['vegan', 'plant-based', 'vegetables', 'fruits', 'nuts', 'grains'],
    seafood: ['fish', 'seafood', 'salmon', 'tuna', 'shrimp', 'lobster']
  };

  /**
   * Parse WhatsApp message to extract carbon tracking data
   */
  static parseMessage(message: string): Partial<CarbonLogData> | null {
    const text = message.toLowerCase().trim();
    
    // Check if this is a carbon tracking message
    const carbonIndicators = ['carbon', 'co2', 'emission', 'footprint', 'track', 'log'];
    const hasIndicator = carbonIndicators.some(indicator => text.includes(indicator));
    
    if (!hasIndicator) {
      return null; // Not a carbon tracking message
    }

    const result: Partial<CarbonLogData> = {
      transport: [],
      home: {},
      food: [],
      goods: '',
      date: new Date().toISOString().split('T')[0]
    };

    // Parse transport data
    result.transport = this.parseTransport(text);

    // Parse home energy data
    result.home = this.parseHome(text);

    // Parse food data
    result.food = this.parseFood(text);

    // Parse goods/services spending
    result.goods = this.parseGoods(text);

    return result;
  }

  /**
   * Parse transport information from message
   */
  private static parseTransport(text: string): TransportEntry[] {
    const transport: TransportEntry[] = [];

    // Look for distance patterns (e.g., "drove 15 km", "20km by car", "flight 500 miles")
    const distancePatterns = [
      /(\d+(?:\.\d+)?)\s*(km|kilometers?|miles?|mi)\s*(?:by\s+)?(\w+)/gi,
      /(\w+)\s+(\d+(?:\.\d+)?)\s*(km|kilometers?|miles?|mi)/gi,
      /(\d+(?:\.\d+)?)\s*(\w+)\s*(km|kilometers?|miles?|mi)/gi
    ];

    for (const pattern of distancePatterns) {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        const [, distance, unit, mode] = match;
        
        // Convert miles to km if needed
        let distanceKm = parseFloat(distance);
        if (unit.toLowerCase().includes('mile') || unit.toLowerCase() === 'mi') {
          distanceKm *= 1.60934;
        }

        // Determine transport mode
        const transportMode = this.getTransportMode(mode.toLowerCase());
        if (transportMode) {
          transport.push({
            mode: transportMode,
            distance: distanceKm.toString()
          });
        }
      }
    }

    return transport;
  }

  /**
   * Parse home energy information from message
   */
  private static parseHome(text: string): HomeEntry {
    const home: HomeEntry = {};

    // Look for electricity usage (e.g., "electricity 45 kwh", "45 units electricity")
    const electricityPattern = /(?:electricity|power|kwh|units)\s*:?\s*(\d+(?:\.\d+)?)/gi;
    const electricityMatch = electricityPattern.exec(text);
    if (electricityMatch) {
      home.electricity = electricityMatch[1];
    }

    // Look for gas usage (e.g., "gas 30 kwh", "30 units gas")
    const gasPattern = /(?:gas|heating)\s*:?\s*(\d+(?:\.\d+)?)/gi;
    const gasMatch = gasPattern.exec(text);
    if (gasMatch) {
      home.gas = gasMatch[1];
    }

    // Look for water usage (e.g., "water 200 liters", "200L water")
    const waterPattern = /(?:water)\s*:?\s*(\d+(?:\.\d+)?)\s*(?:liters?|l|gallons?)/gi;
    const waterMatch = waterPattern.exec(text);
    if (waterMatch) {
      home.water = waterMatch[1];
    }

    return home;
  }

  /**
   * Parse food information from message
   */
  private static parseFood(text: string): FoodEntry[] {
    const food: FoodEntry[] = [];

    // Look for food servings (e.g., "2 servings meat", "3 vegetarian meals")
    const foodPattern = /(\d+(?:\.\d+)?)\s*(?:servings?|meals?|portions?)\s*(?:of\s+)?(\w+)/gi;
    let match;
    while ((match = foodPattern.exec(text)) !== null) {
      const [, servings, foodType] = match;
      const type = this.getFoodType(foodType.toLowerCase());
      if (type) {
        food.push({
          type,
          servings
        });
      }
    }

    // Also look for direct food mentions (e.g., "ate meat", "had 2 chicken meals")
    for (const [type, keywords] of Object.entries(this.FOOD_KEYWORDS)) {
      for (const keyword of keywords) {
        if (text.includes(keyword)) {
          // Try to extract quantity
          const quantityPattern = new RegExp(`(\\d+(?:\\.\\d+)?)\\s*(?:servings?|meals?|portions?)?\\s*(?:of\\s+)?${keyword}`, 'gi');
          const quantityMatch = quantityPattern.exec(text);
          if (quantityMatch) {
            food.push({
              type: type as FoodEntry['type'],
              servings: quantityMatch[1]
            });
          } else {
            // Default to 1 serving if no quantity specified
            food.push({
              type: type as FoodEntry['type'],
              servings: '1'
            });
          }
          break; // Only add once per type
        }
      }
    }

    return food;
  }

  /**
   * Parse goods/services spending from message
   */
  private static parseGoods(text: string): string {
    // Look for spending patterns (e.g., "spent $50", "$25 shopping", "bought $100 worth")
    const spendingPatterns = [
      /(?:spent|shopping|bought|purchased)\s*\$?(\d+(?:\.\d+)?)/gi,
      /\$(\d+(?:\.\d+)?)\s*(?:spent|shopping|goods|services)/gi,
      /(\d+(?:\.\d+)?)\s*(?:dollars?|usd|\$)\s*(?:spent|shopping|goods)/gi
    ];

    for (const pattern of spendingPatterns) {
      const match = pattern.exec(text);
      if (match) {
        return match[1];
      }
    }

    return '';
  }

  /**
   * Get transport mode from keyword
   */
  private static getTransportMode(keyword: string): TransportEntry['mode'] | null {
    for (const [mode, keywords] of Object.entries(this.TRANSPORT_KEYWORDS)) {
      if (keywords.some(k => keyword.includes(k))) {
        return mode as TransportEntry['mode'];
      }
    }
    return null;
  }

  /**
   * Get food type from keyword
   */
  private static getFoodType(keyword: string): FoodEntry['type'] | null {
    for (const [type, keywords] of Object.entries(this.FOOD_KEYWORDS)) {
      if (keywords.some(k => keyword.includes(k))) {
        return type as FoodEntry['type'];
      }
    }
    return null;
  }

  /**
   * Generate helpful response message for WhatsApp
   */
  static generateResponseMessage(data: Partial<CarbonLogData> | null, totalCO2?: number): string {
    if (!data) {
      return `Hi! I can help you track your carbon footprint via WhatsApp! 🌱

Send me messages like:
• "Track carbon: drove 25km today"
• "Carbon log: electricity 45 kwh, gas 30 kwh"
• "Footprint: 2 servings meat, spent $50"
• "Track emissions: flight 500km, water 200L"

I'll automatically calculate your CO2 emissions! 📊`;
    }

    let response = "✅ Carbon data recorded successfully!\n\n";
    
    if (data.transport && data.transport.length > 0) {
      response += "🚗 Transport:\n";
      data.transport.forEach(t => {
        response += `• ${t.mode}: ${t.distance}km\n`;
      });
      response += "\n";
    }

    if (data.home && Object.keys(data.home).length > 0) {
      response += "🏠 Home Energy:\n";
      if (data.home.electricity) response += `• Electricity: ${data.home.electricity} kWh\n`;
      if (data.home.gas) response += `• Gas: ${data.home.gas} kWh\n`;
      if (data.home.water) response += `• Water: ${data.home.water} L\n`;
      response += "\n";
    }

    if (data.food && data.food.length > 0) {
      response += "🍽️ Food:\n";
      data.food.forEach(f => {
        response += `• ${f.type}: ${f.servings} servings\n`;
      });
      response += "\n";
    }

    if (data.goods && parseFloat(data.goods) > 0) {
      response += `💰 Goods & Services: $${data.goods}\n\n`;
    }

    if (totalCO2) {
      response += `📊 Total CO2: ${totalCO2.toFixed(2)} kg\n\n`;
    }

    response += "View your full dashboard at your Myǃhū app! 🌍";

    return response;
  }

  /**
   * Calculate CO2 emissions from parsed data
   */
  static calculateCO2(data: Partial<CarbonLogData>): number {
    let total = 0;

    // Transport CO2 calculation
    if (data.transport) {
      const transportFactors = {
        car: 0.21,
        bus: 0.09,
        flight: 0.255,
        bike: 0,
        walk: 0,
        train: 0.041
      };

      data.transport.forEach(t => {
        const distance = parseFloat(t.distance) || 0;
        total += distance * transportFactors[t.mode];
      });
    }

    // Home energy CO2 calculation
    if (data.home) {
      total += (parseFloat(data.home.electricity || '0') * 0.4); // 0.4 kg CO2/kWh
      total += (parseFloat(data.home.gas || '0') * 0.2); // 0.2 kg CO2/kWh
      total += (parseFloat(data.home.water || '0') * 0.0003); // 0.0003 kg CO2/liter
    }

    // Food CO2 calculation
    if (data.food) {
      const foodFactors = {
        meat: 5,
        vegetarian: 2,
        vegan: 1.5,
        seafood: 3
      };

      data.food.forEach(f => {
        const servings = parseFloat(f.servings) || 0;
        total += servings * foodFactors[f.type];
      });
    }

    // Goods & services CO2 calculation
    if (data.goods) {
      total += (parseFloat(data.goods) * 0.5); // 0.5 kg CO2 per $ spent
    }

    return total;
  }
}
